import { ButtonColors } from '@chiroup/components/Button';
import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { OpenClosedStates } from '@chiroup/core/enums/OpenClosedStates.enum';
import React, { useState } from 'react';
import Modal from '../../../../common/Modal';
import EditableChartCell from './EditableChartCell';
import InlineAssessmentInput from './InlineAssessmentInput';

type Props = {
  isOpen: boolean;
  close: () => void;
  value: any;
  colHeadings: { text: string; normalDefaultValue: string }[];
  rowHeadings: string[];
  tdChoice: { [key: string]: { text: string }[] };
  noDropdown?: string[];
  title: string;
  abbreviations?: { [key: string]: string };
  onChange: (value?: any) => void;
  wideCols?: { [key: string]: boolean };
  test: string;
  signed?: boolean;
};

const EditableChart: React.FC<Props> = ({
  isOpen,
  close,
  value,
  colHeadings,
  rowHeadings,
  tdChoice,
  noDropdown,
  title,
  abbreviations,
  onChange,
  wideCols,
  test,
  signed,
}) => {
  const [confirmOpen, setConfirmOpen] = useState<OpenClosedStates>(OpenClosedStates.Closed);
  const [inlineOtherInput, setInlineOtherInput] = useState<{
    row: string;
    col: string;
    side: string;
    value: string;
    onSubmit: (explanation: string) => void;
  } | null>(null);

  const clearValues = () => {
    onChange({ result: value?.result });
  };

  const normalValues = (val: string) => {
    return (
      val === 'Normal' ||
      val === 'Not assessed' ||
      val === 'Intact' ||
      val === 'Grade 5: Normal strength' ||
      val === 'Not assessed' ||
      val === '2+: Normal'
    );
  };

  const getOverallResult = (val: string, passedRow: string, passedCol: string, side: string) => {
    if (!normalValues(val)) {
      return 'abnormal';
    }

    for (const row of rowHeadings) {
      for (const col of colHeadings) {
        if (passedRow === row && passedCol === col.text) {
          const otherSide = side === 'right' ? 'left' : 'right';
          const otherChoice = value?.detail?.[row]?.[col.text]?.[otherSide]?.choice;

          if (otherChoice && !normalValues(otherChoice)) {
            return 'abnormal';
          }
        } else {
          const leftChoice = value?.detail?.[row]?.[col.text]?.left?.choice;

          if (leftChoice && !normalValues(leftChoice)) {
            return 'abnormal';
          }

          const rightChoice = value?.detail?.[row]?.[col.text]?.right?.choice;

          if (rightChoice && !normalValues(rightChoice)) {
            return 'abnormal';
          }
        }
      }
    }

    return 'normal';
  };

  const onChangeItem = (row: string, col: string, side: string) => (val: string) => {
    if (signed) return;
    const overallResult = getOverallResult(val, row, col, side);

    onChange({
      ...value,
      detail: {
        ...value?.detail,
        [row]: {
          ...value?.detail?.[row],
          [col]: {
            ...value?.detail?.[row]?.[col],
            [side]: {
              ...value?.detail?.[row]?.[col]?.[side],
              choice: val,
            },
          },
        },
      },
      result: overallResult,
    });

    if (val === 'Other') {
      setInlineOtherInput({
        row,
        col,
        side,
        value: value?.detail?.[row]?.[col]?.[side]?.explanation || '',
        onSubmit: (explanation: string) => {
          onChange({
            ...value,
            detail: {
              ...value?.detail,
              [row]: {
                ...value?.detail?.[row],
                [col]: {
                  ...value?.detail?.[row]?.[col],
                  [side]: {
                    ...value?.detail?.[row]?.[col]?.[side],
                    choice: val,
                    explanation,
                  },
                },
              },
            },
            result: 'abnormal',
          });
        },
      });

      return;
    }
  };

  const checkOverall = () => {
    for (const row of rowHeadings) {
      for (const col of colHeadings) {
        const leftChoice = value?.detail?.[row]?.[col.text]?.left?.choice;

        if (leftChoice && !normalValues(leftChoice)) {
          return 'abnormal';
        }

        const rightChoice = value?.detail?.[row]?.[col.text]?.right?.choice;

        if (rightChoice && !normalValues(rightChoice)) {
          return 'abnormal';
        }
      }
    }

    return 'normal';
  };

  const closeModal = () => {
    const overAll = checkOverall();
    if (value?.result === overAll || signed) return close();
    setConfirmOpen(OpenClosedStates.Open);
  };

  const confirm = () => {
    setConfirmOpen(OpenClosedStates.Closed);
    onChange({
      ...value,
      result: 'normal',
    });
    close();
  };

  return (
    <Modal
      isOpen={isOpen}
      close={closeModal}
      size="3xl"
      header={{
        title: title || '',
      }}
      footer={{
        buttons: [
          {
            text: 'Clear All Values',
            onClick: clearValues,
          },
          {
            text: 'Close',
            onClick: closeModal,
            color: ButtonColors.primary,
          },
        ],
      }}
    >
      <div className="w-full">
        <table className="min-w-[380px] overflow-x-auto divide-y divide-gray-300 w-full border">
          <thead className=" dark:bg-dark-gray-900">
            <tr className="dark:bg-dark-600">
              <th></th>
              {colHeadings.map(heading => {
                return (
                  <th
                    scope="col"
                    colSpan={2}
                    className="text-center border-b border-l"
                    key={heading.text}
                  >
                    {heading.text.charAt(0).toUpperCase() + heading.text.slice(1)}
                  </th>
                );
              })}
            </tr>
            <tr className="dark:bg-dark-600">
              <th></th>
              {colHeadings.map((heading, i) => {
                return (
                  <React.Fragment key={`${heading.text}{i}`}>
                    <th
                      scope="col"
                      colSpan={1}
                      className={[
                        'text-center border-r border-l',
                        wideCols?.[heading.text] ? 'w-20' : ' ',
                      ].join(' ')}
                      key={`Left${i}`}
                    >
                      Left
                    </th>
                    <th
                      scope="col"
                      colSpan={1}
                      className={['text-center', wideCols?.[heading.text] ? 'w-20' : ''].join(' ')}
                      key={`Right${i}`}
                    >
                      Right
                    </th>
                  </React.Fragment>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-300 bg-white dark:bg-dark-gray-900 border">
            {rowHeadings.map((rowHeading, i) => {
              return (
                <tr key={`${rowHeading}${i}`}>
                  <th
                    className={`w-1/5 dark:bg-dark-gray-900 ${
                      rowHeading.length > 2 ? 'w-16' : 'w-4'
                    } pl-1 pr-1 border-r`}
                  >
                    {rowHeading.charAt(0).toUpperCase() + rowHeading.slice(1)}
                  </th>
                  {colHeadings.map((colHeading, colIndex) => {
                    const isEdittingLeft =
                      inlineOtherInput?.row === rowHeading &&
                      inlineOtherInput?.col === colHeading.text &&
                      inlineOtherInput?.side === 'left';
                    const isEdittingRight =
                      inlineOtherInput?.row === rowHeading &&
                      inlineOtherInput?.col === colHeading.text &&
                      inlineOtherInput?.side === 'right';

                    return !noDropdown?.includes(`${rowHeading}${colHeading.text}`) ? (
                      <React.Fragment key={`${rowHeading}${colHeading.text}${colIndex}`}>
                        <td className="w-1/5">
                          <div className="flex items-center justify-center">
                            <EditableChartCell
                              onChange={onChangeItem(rowHeading, colHeading.text, 'left')}
                              value={value?.detail?.[rowHeading]?.[colHeading.text]?.left}
                              colHeading={colHeading}
                              rowHeading={rowHeading}
                              tdChoice={tdChoice}
                              abbreviations={abbreviations}
                              i={colIndex}
                            />

                            {inlineOtherInput && isEdittingLeft && (
                              <InlineAssessmentInput
                                value={inlineOtherInput.value}
                                inputClassName="w-full"
                                onChange={val =>
                                  setInlineOtherInput({ ...inlineOtherInput, value: val })
                                }
                                onSubmit={() => {
                                  inlineOtherInput.onSubmit(inlineOtherInput.value);
                                  setInlineOtherInput(null);
                                }}
                              />
                            )}
                          </div>
                        </td>
                        <td className="w-1/5">
                          <div className="flex items-center justify-center">
                            <EditableChartCell
                              onChange={onChangeItem(rowHeading, colHeading.text, 'right')}
                              value={value?.detail?.[rowHeading]?.[colHeading.text]?.right}
                              colHeading={colHeading}
                              rowHeading={rowHeading}
                              tdChoice={tdChoice}
                              abbreviations={abbreviations}
                              i={colIndex}
                            />

                            {inlineOtherInput && isEdittingRight && (
                              <InlineAssessmentInput
                                value={inlineOtherInput.value}
                                inputClassName="w-full"
                                onChange={val =>
                                  setInlineOtherInput({ ...inlineOtherInput, value: val })
                                }
                                onSubmit={() => {
                                  inlineOtherInput.onSubmit(inlineOtherInput.value);
                                  setInlineOtherInput(null);
                                }}
                              />
                            )}
                          </div>
                        </td>
                      </React.Fragment>
                    ) : (
                      <React.Fragment key={`${rowHeading}${colHeading.text}${colIndex}`}>
                        <td className="bg-dark-gray-50 dark:bg-dark-gray-600"></td>
                        <td className="bg-dark-gray-50 dark:bg-dark-gray-600"></td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        <ConfirmModal
          isOpen={confirmOpen === OpenClosedStates.Open}
          confirm={confirm}
          close={() => setConfirmOpen(OpenClosedStates.Closed)}
          confirmText="Confirm"
          description="No individual tests have been marked abnormal. The overall test result will be changed back to normal."
        />
      </div>
    </Modal>
  );
};

export default EditableChart;
